import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Zap, Archive, Plus, XCircle } from "lucide-react";
import { useCreateTask } from '@/context/CreateTaskContext';
import { TbCircleCheck } from 'react-icons/tb';
import { useAuth } from "@/context/AuthContext";
import { FiRefreshCw, FiSearch, FiX, FiFilter, FiChevronLeft, FiChevronRight, FiUser, FiClock, FiAlertCircle, FiArrowUp, FiArrowDown, FiPackage } from 'react-icons/fi';
import { toast } from 'sonner';
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { getButtonActiveColor, STATUS_COLORS, getReleaseStatusColor, LABEL_COLOR_MAP } from "@/lib/colorsMap";
import { getIconComponent } from "@/lib/statusIconsMap";
import { getPriorityConfig } from "@/lib/priorityMap";
import StatusBotIcon from '@/components/StatusBotIcon';
import { TaskContextMenu } from '@/components/TaskContextMenu';
import React from 'react';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

// URL state management helpers
const serializeFilters = (filters) => {
    const params = new URLSearchParams();
    
    // Search term
    if (filters.searchTerm) params.set('q', filters.searchTerm);
    
    // Quick filters (booleans)
    if (filters.myTasksOnly) params.set('myTasks', '1');
    if (filters.overdueOnly) params.set('overdue', '1');
    if (filters.dueSoonOnly) params.set('dueSoon', '1');
    if (filters.activeWeekOnly) params.set('activeWeek', '1');
    if (filters.selectedScopes?.length) params.set('scopes', filters.selectedScopes.join('~'));
    
    // Advanced filters (arrays) - using ~ as separator for cleaner URLs
    if (filters.selectedOrganizations?.length) params.set('orgs', filters.selectedOrganizations.join('~'));
    if (filters.selectedProjects?.length) params.set('projects', filters.selectedProjects.join('~'));
    if (filters.selectedAssignees?.length) params.set('assignees', filters.selectedAssignees.join('~'));
    if (filters.selectedStatuses?.length) params.set('statuses', filters.selectedStatuses.join('~'));
    if (filters.selectedPriorities?.length) params.set('priorities', filters.selectedPriorities.join('~'));
    if (filters.selectedLabels?.length) params.set('labels', filters.selectedLabels.join('~'));
    if (filters.selectedReleases?.length) params.set('releases', filters.selectedReleases.join('~'));
    
    // Sort
    if (filters.sortBy && filters.sortBy !== 'updated') params.set('sortBy', filters.sortBy);
    if (filters.sortOrder && filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);
    
    // Pagination
    if (filters.currentPage > 1) params.set('page', filters.currentPage.toString());
    
    return params;
};

const deserializeFilters = (searchParams) => {
    return {
        searchTerm: searchParams.get('q') || '',
        myTasksOnly: searchParams.get('myTasks') === '1',
        overdueOnly: searchParams.get('overdue') === '1',
        dueSoonOnly: searchParams.get('dueSoon') === '1',
        activeWeekOnly: searchParams.get('activeWeek') === '1',
        selectedScopes: searchParams.get('scopes')?.split('~').filter(Boolean) || [],
        selectedOrganizations: searchParams.get('orgs')?.split('~').filter(Boolean) || [],
        selectedProjects: searchParams.get('projects')?.split('~').filter(Boolean) || [],
        selectedAssignees: searchParams.get('assignees')?.split('~').filter(Boolean) || [],
        selectedStatuses: searchParams.get('statuses')?.split('~').filter(Boolean) || [],
        selectedPriorities: searchParams.get('priorities')?.split('~').filter(Boolean) || [],
        selectedLabels: searchParams.get('labels')?.split('~').filter(Boolean) || [],
        selectedReleases: searchParams.get('releases')?.split('~').filter(Boolean) || [],
        sortBy: searchParams.get('sortBy') || 'updated',
        sortOrder: searchParams.get('sortOrder') || 'desc',
        currentPage: parseInt(searchParams.get('page') || '1', 10),
    };
};

const Tasks = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const isInitialized = useRef(false);
    const scrollContainerRef = useRef(null);
    
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [showRightShadow, setShowRightShadow] = useState(false);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    
    // Initialize state from URL or defaults
    const urlFilters = !isInitialized.current ? deserializeFilters(searchParams) : null;
    
    const [searchTerm, setSearchTerm] = useState(urlFilters?.searchTerm || '');
    
    // Quick filters
    const [myTasksOnly, setMyTasksOnly] = useState(urlFilters?.myTasksOnly || false);
    const [overdueOnly, setOverdueOnly] = useState(urlFilters?.overdueOnly || false);
    const [dueSoonOnly, setDueSoonOnly] = useState(urlFilters?.dueSoonOnly || false);
    
    // Advanced filters
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [selectedOrganizations, setSelectedOrganizations] = useState(urlFilters?.selectedOrganizations || []);
    const [selectedProjects, setSelectedProjects] = useState(urlFilters?.selectedProjects || []);
    const [selectedAssignees, setSelectedAssignees] = useState(urlFilters?.selectedAssignees || []);
    const [selectedStatuses, setSelectedStatuses] = useState(urlFilters?.selectedStatuses || []);
    const [selectedPriorities, setSelectedPriorities] = useState(urlFilters?.selectedPriorities || []);
    const [selectedLabels, setSelectedLabels] = useState(urlFilters?.selectedLabels || []);
    const [selectedReleases, setSelectedReleases] = useState(urlFilters?.selectedReleases || []);
    const [activeWeekOnly, setActiveWeekOnly] = useState(urlFilters?.activeWeekOnly || false);
    const [selectedScopes, setSelectedScopes] = useState(urlFilters?.selectedScopes || []);
    
    // Sort options
    const [sortBy, setSortBy] = useState(urlFilters?.sortBy || 'updated');
    const [sortOrder, setSortOrder] = useState(urlFilters?.sortOrder || 'desc');
    
    // Bot Sort info modal
    const [showBotSortInfo, setShowBotSortInfo] = useState(false);
    
    // Track if user has seen the Bot Sort info before (using localStorage)
    const hasSeenBotSortInfo = useRef(localStorage.getItem('hasSeenBotSortInfo') === 'true');
    
    // Show Bot Sort info modal when user selects it for the first time
    useEffect(() => {
        if (sortBy === 'botSort' && !hasSeenBotSortInfo.current) {
            setShowBotSortInfo(true);
        }
    }, [sortBy]);
    
    const handleCloseBotSortInfo = () => {
        setShowBotSortInfo(false);
        localStorage.setItem('hasSeenBotSortInfo', 'true');
        hasSeenBotSortInfo.current = true;
    };
    
    // Filter options (from context)
    const [filterOptions, setFilterOptions] = useState({
        organizations: [],
        projects: [],
        users: [],
        statuses: [],
        priorities: [],
        releases: [],
        labels: [],
    });
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(urlFilters?.currentPage || 1);
    const [pageSize, setPageSize] = useState(50);
    const [pagination, setPagination] = useState({
        page: 1,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
        total: 0
    });
    
    const { axiosInstance, user } = useAuth();
    
    // Create task dialog from context
    const { openCreateTaskDialog } = useCreateTask();
    
    // Get color classes for the user's avatar color
    const userColors = getButtonActiveColor(user?.avatarColor);
    
    // Helper to normalize label color (strip bg- prefix and -500 suffix)
    const normalizeLabelColor = (color) => {
        if (!color) return 'gray';
        // Remove bg- prefix and any -XXX suffix (like -500, -600)
        return color.replace(/^bg-/, '').replace(/-\d+$/, '');
    };
    
    // Helper to determine release status from release data
    const getReleaseStatus = (release) => {
        if (!release) return 'upcoming';
        
        // Check if explicitly shipped (actualReleaseDate exists)
        if (release.actualReleaseDate) return 'shipped';
        
        const now = new Date();
        const start = release.startDate ? new Date(release.startDate) : null;
        const target = release.targetDate ? new Date(release.targetDate) : null;
        
        // If past target date (and has targetDate), it's overdue
        if (target && now > target) return 'overdue';
        
        // If has startDate and we're past it, it's active
        if (start && now >= start) return 'active';
        
        // Default to upcoming (future startDate or no startDate)
        return 'upcoming';
    };
    
    // Helper to check if a task was completed in a past week
    const isCompletedInPastWeek = (task) => {
        if (!task.completedAt) return false;
        
        // Get start of current week (Sunday)
        const now = new Date();
        const currentWeekStart = new Date(now);
        currentWeekStart.setDate(now.getDate() - now.getDay());
        currentWeekStart.setHours(0, 0, 0, 0);
        
        const completedDate = new Date(task.completedAt);
        
        // If completed before this week started, it's from a past week
        return completedDate < currentWeekStart;
    };
    
    const fetchTasks = async (page = currentPage) => {
        setLoading(true);
        setRefreshing(true);
        try {
            const offset = (page - 1) * pageSize;
            
            // Build filters object
            const filters = {};
            
            // Quick filters
            if (myTasksOnly) filters.assigneeId = 'me';
            if (overdueOnly) filters.overdue = true;
            if (dueSoonOnly) filters.dueSoon = true;
            
            // Advanced filters
            if (selectedOrganizations.length > 0) filters.organizationSlug = selectedOrganizations;
            if (selectedProjects.length > 0) filters.projectId = selectedProjects;
            // Only apply multi-select assignees if "My Tasks" is not active
            if (selectedAssignees.length > 0 && !myTasksOnly) filters.assigneeId = selectedAssignees;
            if (selectedStatuses.length > 0) {
                filters.status = selectedStatuses;
            }
            if (selectedPriorities.length > 0) filters.priority = selectedPriorities;
            if (selectedLabels.length > 0) filters.labels = selectedLabels;
            if (selectedReleases.length > 0) filters.releaseId = selectedReleases;
            // When activeWeekOnly is OFF (default), include completed tasks
            if (!activeWeekOnly) filters.includeCompleted = true;
            // Scope filtering: Active Week, Backlog, Closed
            if (selectedScopes.length > 0) {
                filters.scopes = selectedScopes;
                // If 'closed' is not in selected scopes, exclude closed tasks
                if (!selectedScopes.includes('closed')) {
                    filters.includeClosed = false;
                } else {
                    filters.includeClosed = true;
                }
            } else {
                // No scope filter = show everything including closed
                filters.includeClosed = true;
            }
            
            console.log('Built filters:', filters);
            
            const requestBody = {
                query: searchTerm,
                filters,
                options: {
                    limit: pageSize,
                    offset: offset,
                    sortBy: sortBy,
                    sortOrder: sortOrder
                }
            };
            
            const response = await axiosInstance.post('/tasks/search', requestBody);
            
            // Handle the new search helper response format
            const { 
                tasks: tasksData = [], 
                total = 0, 
                pagination: paginationData = {}, 
                metadata = {} 
            } = response.data;
            
            console.log('Search Results:', {
                total,
                strategy: metadata.strategy,
                matchType: metadata.matchType,
                tasksCount: tasksData.length,
                pagination: paginationData
            });
            
            
            setTasks(tasksData);
            setPagination({
                ...paginationData,
                total
            });
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            toast.error('Error fetching tasks, please try again later');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchTasks(1);
        fetchFilterOptions();
        // Mark as initialized after first fetch
        isInitialized.current = true;
    }, []);

    // Fetch available filter options
    const fetchFilterOptions = async () => {
        try {
            const response = await axiosInstance.get('/tasks/search/context');
            setFilterOptions(response.data);
            console.log('Filter options loaded:', response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            toast.error('Failed to load filter options');
        }
    };

    // Refetch when filters change (reset to page 1)
    useEffect(() => {
        // Debounce search to avoid too many API calls
        const timeoutId = setTimeout(() => {
            setCurrentPage(1); // Reset to page 1 when filters change
            fetchTasks(1);
        }, searchTerm ? 300 : 0); // 300ms debounce for search, immediate for other filters

        return () => clearTimeout(timeoutId);
    }, [
        searchTerm, 
        myTasksOnly, 
        overdueOnly, 
        dueSoonOnly,
        selectedOrganizations,
        selectedProjects,
        selectedAssignees,
        selectedStatuses,
        selectedPriorities,
        selectedLabels,
        selectedReleases,
        activeWeekOnly,
        selectedScopes,
        sortBy,
        sortOrder
    ]);
    
    // Sync filters to URL (after initialization to avoid infinite loops)
    useEffect(() => {
        if (!isInitialized.current) return; // Skip during initialization
        
        const params = serializeFilters({
            searchTerm,
            myTasksOnly,
            overdueOnly,
            dueSoonOnly,
            activeWeekOnly,
            selectedScopes,
            selectedOrganizations,
            selectedProjects,
            selectedAssignees,
            selectedStatuses,
            selectedPriorities,
            selectedLabels,
            selectedReleases,
            sortBy,
            sortOrder,
            currentPage
        });
        
        // Only update if params changed (prevents unnecessary history entries)
        const currentParams = searchParams.toString();
        const newParams = params.toString();
        if (currentParams !== newParams) {
            setSearchParams(params, { replace: true });
        }
    }, [
        searchTerm,
        myTasksOnly,
        overdueOnly,
        dueSoonOnly,
        activeWeekOnly,
        selectedScopes,
        selectedOrganizations,
        selectedProjects,
        selectedAssignees,
        selectedStatuses,
        selectedPriorities,
        selectedLabels,
        selectedReleases,
        sortBy,
        sortOrder,
        currentPage
    ]);
    
    // Clear all filters
    const clearAllFilters = () => {
        setSearchTerm('');
        setMyTasksOnly(false);
        setOverdueOnly(false);
        setDueSoonOnly(false);
        setSelectedOrganizations([]);
        setSelectedProjects([]);
        setSelectedAssignees([]);
        setSelectedStatuses([]);
        setSelectedPriorities([]);
        setSelectedLabels([]);
        setSelectedReleases([]);
        setActiveWeekOnly(false);
        setSelectedScopes([]);
        // setSortBy('updated');
        // setSortOrder('desc');
        setCurrentPage(1);
        setFiltersOpen(false);
    };
    
    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0;
        if (myTasksOnly) count++;
        if (overdueOnly) count++;
        if (dueSoonOnly) count++;
        if (selectedOrganizations.length > 0) count++;
        if (selectedProjects.length > 0) count++;
        if (selectedAssignees.length > 0) count++;
        if (selectedStatuses.length > 0) count++;
        if (selectedPriorities.length > 0) count++;
        if (selectedLabels.length > 0) count++;
        if (selectedReleases.length > 0) count++;
        if (activeWeekOnly) count++;
        if (selectedScopes.length > 0) count++;
        return count;
    };
    
    const activeFilterCount = getActiveFilterCount();

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        fetchTasks(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const fadeInVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 }
    };

    // Helper to get filter details for badges
    const getActiveFilterDetails = () => {
        const details = [];
        
        if (myTasksOnly) {
            details.push({ id: 'myTasks', label: 'My Tasks', color: userColors.indicator, onRemove: () => setMyTasksOnly(false) });
        }
        if (overdueOnly) {
            details.push({ id: 'overdue', label: 'Overdue', color: 'text-red-600 dark:text-red-400', onRemove: () => setOverdueOnly(false) });
        }
        if (dueSoonOnly) {
            details.push({ id: 'dueSoon', label: 'Due Soon', color: 'text-amber-600 dark:text-amber-400', onRemove: () => setDueSoonOnly(false) });
        }
        
        if (activeWeekOnly) {
            details.push({ id: 'activeWeek', label: 'Active week only', color: 'text-emerald-600 dark:text-emerald-400', onRemove: () => setActiveWeekOnly(false) });
        }
        
        selectedScopes.forEach(scope => {
            const scopeLabels = {
                'active': 'Active Week',
                'backlog': 'Backlog',
                'closed': 'Closed'
            };
            details.push({ 
                id: `scope-${scope}`, 
                label: scopeLabels[scope] || scope,
                type: 'Scope',
                onRemove: () => setSelectedScopes(prev => prev.filter(s => s !== scope))
            });
        });
        
        selectedOrganizations.forEach(slug => {
            const org = filterOptions.organizations.find(o => o.slug === slug);
            if (org) {
                details.push({ 
                    id: `org-${slug}`, 
                    label: org.name, 
                    type: 'Organization',
                    onRemove: () => setSelectedOrganizations(prev => prev.filter(o => o !== slug)) 
                });
            }
        });
        
        selectedProjects.forEach(id => {
            const project = filterOptions.projects.find(p => p.id === id);
            if (project) {
                details.push({ 
                    id: `project-${id}`, 
                    label: `${project.name} (${project.key})`, 
                    type: 'Project',
                    onRemove: () => setSelectedProjects(prev => prev.filter(p => p !== id)) 
                });
            }
        });
        
        selectedAssignees.forEach(id => {
            if (id === 'unassigned') {
                details.push({ 
                    id: 'assignee-unassigned', 
                    label: 'Unassigned', 
                    type: 'Assignee',
                    onRemove: () => setSelectedAssignees(prev => prev.filter(a => a !== 'unassigned')) 
                });
            } else {
                const user = filterOptions.users.find(u => u.id === id);
                if (user) {
                    details.push({ 
                        id: `assignee-${id}`, 
                        label: user.name, 
                        type: 'Assignee',
                        onRemove: () => setSelectedAssignees(prev => prev.filter(a => a !== id)) 
                    });
                }
            }
        });
        
        selectedStatuses.forEach(statusName => {
            const status = filterOptions.statuses.find(s => s.value === statusName);
            if (status) {
                const IconComponent = getIconComponent(status.icon);
                const colorClass = STATUS_COLORS[status.color]?.text || 'text-gray-500';
                
                details.push({ 
                    id: `status-${statusName}`, 
                    label: status.label,
                    icon: IconComponent,
                    iconColor: colorClass,
                    type: 'Status',
                    onRemove: () => setSelectedStatuses(prev => prev.filter(s => s !== statusName)) 
                });
            }
        });
        
        selectedPriorities.forEach(value => {
            const priority = filterOptions.priorities.find(p => p.value === value);
            if (priority) {
                const priorityConfig = getPriorityConfig(value);
                details.push({ 
                    id: `priority-${value}`, 
                    label: priority.label,
                    icon: priorityConfig.icon,
                    iconColor: priorityConfig.color,
                    type: 'Priority',
                    onRemove: () => setSelectedPriorities(prev => prev.filter(p => p !== value)) 
                });
            }
        });
        
        selectedReleases.forEach(id => {
            const release = filterOptions.releases.find(r => r.id === id);
            if (release) {
                details.push({ 
                    id: `release-${id}`, 
                    label: `${release.name} (${release.projectKey})`, 
                    type: 'Release',
                    onRemove: () => setSelectedReleases(prev => prev.filter(r => r !== id)) 
                });
            }
        });
        
        selectedLabels.forEach(labelName => {
            const label = filterOptions.labels.find(l => l.name === labelName);
            if (label) {
                details.push({ 
                    id: `label-${labelName}`, 
                    label: label.name, 
                    type: 'Label',
                    onRemove: () => setSelectedLabels(prev => prev.filter(l => l !== labelName)) 
                });
            }
        });
        
        return details;
    };
    

    const activeFilters = getActiveFilterDetails();
    
    // Check if scrolling is needed and update shadow visibility
    const updateScrollShadow = () => {
        if (scrollContainerRef.current) {
            const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
            
            // Show right shadow if there's more content to scroll to on the right
            setShowRightShadow(scrollWidth > clientWidth && scrollLeft + clientWidth < scrollWidth);
            
            // Show left shadow if scrolled away from the left edge
            setShowLeftShadow(scrollLeft > 0);
        }
    };
    
    // Set up scroll event listener
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', updateScrollShadow);
            // Initial check
            updateScrollShadow();
            
            // Check again after a short delay to ensure content is fully rendered
            const timer = setTimeout(updateScrollShadow, 100);
            
            return () => {
                scrollContainer.removeEventListener('scroll', updateScrollShadow);
                clearTimeout(timer);
            };
        }
    }, [tasks]);

    // Also check on window resize
    useEffect(() => {
        window.addEventListener('resize', updateScrollShadow);
        return () => {
            window.removeEventListener('resize', updateScrollShadow);
        };
    }, []);

    return (
        <Layout>
            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5 }}
                className="space-y-4 mb-8"
            >
                {/* Header Section */}
                <div className="space-y-4">
                    {/* Top Row - Title and Stats */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-semibold">Tasks</h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {pagination.total > 0 ? (
                                    <>
                                        {pagination.total.toLocaleString()} {pagination.total === 1 ? 'task' : 'tasks'} found
                                        {currentPage > 1 && (
                                            <span className="ml-1">
                                                • Page {currentPage} of {pagination.totalPages}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    'No tasks found'
                                )}
                            </p>
                        </div>
                        
                        {/* Sort and Create Controls */}
                        <div className="flex items-center gap-2">
                            {/* Sort Dropdown */}
                            <Select 
                                value={`${sortBy}-${sortOrder}`} 
                                onValueChange={(value) => {
                                    if (value.endsWith('-asc') || value.endsWith('-desc')) {
                                        const [field, order] = value.split('-');
                                        setSortBy(field);
                                        setSortOrder(order);
                                    }
                                }}
                            >
                                <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs">
                                    <div className="flex items-center gap-1.5">
                                        {sortBy === 'botSort' && <StatusBotIcon className="w-3.5 h-3.5" />}
                                        <span>
                                            {sortBy === 'botSort' ? 'Bot Sort' : 
                                             sortBy === 'updated' ? 'Last updated' :
                                             sortBy === 'created' ? 'Created' :
                                             sortBy === 'dueDate' ? 'Due date' :
                                             sortBy === 'priority' ? 'Priority' : sortBy}
                                        </span>
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={`botSort-${sortOrder}`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">
                                        <div className="flex items-center gap-1.5">
                                            <StatusBotIcon className="w-3.5 h-3.5" />
                                            <span>Bot Sort</span>
                                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 font-normal">Beta</Badge>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value={`updated-${sortOrder}`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">Last updated</SelectItem>
                                    <SelectItem value={`created-${sortOrder}`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">Created</SelectItem>
                                    <SelectItem value={`dueDate-${sortOrder}`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">Due date</SelectItem>
                                    <SelectItem value={`priority-${sortOrder}`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">Priority</SelectItem>
                                    
                                    <Separator className="my-1" />
                                    
                                    <SelectItem value={`${sortBy}-asc`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">
                                        <div className="flex items-center justify-between w-full min-w-[120px]">
                                            <span className="text-xs">Ascending</span>
                                            <FiArrowUp className="h-3.5 w-3.5 text-muted-foreground" />
                                        </div>
                                    </SelectItem>
                                    <SelectItem value={`${sortBy}-desc`} className="data-[state=checked]:bg-transparent data-[state=checked]:text-foreground">
                                        <div className="flex items-center justify-between w-full min-w-[120px]">
                                            <span className="text-xs">Descending</span>
                                            <FiArrowDown className="h-3.5 w-3.5 text-muted-foreground" />
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            
                            {/* Vertical separator */}
                            <div className="h-8 w-px bg-border" />
                            
                            {/* Create Task Button */}
                            <Button
                                size="sm"
                                className="h-8"
                                onClick={() => {
                                    openCreateTaskDialog({
                                        onSuccess: () => {
                                            // Refresh the tasks list
                                            fetchTasks();
                                        }
                                    });
                                }}
                            >
                                <Plus className="h-4 w-4" />
                                <span className="ml-1.5">New Task</span>
                            </Button>
                        </div>
                    </div>
                    
                    {/* Filter Bar */}
                    <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                        {/* Search and Quick Filters */}
                        <div className="flex flex-wrap items-center gap-2 flex-1">
                            {/* Search Input */}
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 pointer-events-none" />
                                <Input
                                    placeholder="Search tasks..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 pr-8 h-9 w-full sm:w-64 text-sm transition-all duration-200 focus:ring-2 focus:ring-violet-500/20"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded hover:bg-muted/50"
                                    >
                                        <FiX className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>

                            {/* My Tasks */}
                            <button 
                                onClick={() => setMyTasksOnly(!myTasksOnly)}
                                className={cn(
                                    "flex items-center gap-1.5 px-3 h-9 rounded-md border transition-all duration-200 whitespace-nowrap text-sm font-medium",
                                    myTasksOnly 
                                        ? `${userColors.button} shadow-sm`
                                        : "border-muted-foreground/20 hover:bg-muted/50 hover:border-muted-foreground/30 bg-background"
                                )}
                            >
                                <FiUser className="h-4 w-4 flex-shrink-0" />
                                <span className="text-xs">My Tasks</span>
                            </button>
                            
                            {/* Overdue */}
                                        <button 
                                onClick={() => setOverdueOnly(!overdueOnly)}
                                            className={cn(
                                    "flex items-center gap-1.5 px-3 h-9 rounded-md border transition-all duration-200 whitespace-nowrap text-sm font-medium",
                                    overdueOnly 
                                        ? "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/50 dark:text-red-300 shadow-sm"
                                        : "border-muted-foreground/20 hover:bg-muted/50 hover:border-muted-foreground/30 bg-background"
                                )}
                            >
                                <FiAlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span className="text-xs">Overdue</span>
                                        </button>
                            
                            {/* Due Soon */}
                                        <button 
                                onClick={() => setDueSoonOnly(!dueSoonOnly)}
                                            className={cn(
                                    "flex items-center gap-1.5 px-3 h-9 rounded-md border transition-all duration-200 whitespace-nowrap text-sm font-medium",
                                    dueSoonOnly 
                                        ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-300 shadow-sm"
                                        : "border-muted-foreground/20 hover:bg-muted/50 hover:border-muted-foreground/30 bg-background"
                                )}
                            >
                                <FiClock className="h-4 w-4 flex-shrink-0" />
                                <span className="text-xs">Due Soon</span>
                                        </button>
                            

                            {/* Advanced Filters Sheet */}
                            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                                <SheetTrigger asChild>
                                    <button
                                        className={cn(
                                            "flex items-center gap-1.5 px-3 h-9 rounded-md border transition-all duration-200 whitespace-nowrap text-sm font-medium relative",
                                            filtersOpen
                                                ? "border-foreground/30 bg-muted shadow-sm"
                                                : "border-muted-foreground/20 hover:bg-muted/50 hover:border-muted-foreground/30 bg-background"
                                        )}
                                    >
                                                        <FiFilter className="h-4 w-4 flex-shrink-0" />
                                                        <span className="text-xs">More Filters</span>
                                                        {(selectedOrganizations.length + selectedProjects.length + selectedAssignees.length + selectedStatuses.length + selectedPriorities.length + selectedReleases.length + selectedLabels.length + selectedScopes.length) > 0 && (
                                                            <Badge variant="secondary" className="ml-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                                                                {selectedOrganizations.length + selectedProjects.length + selectedAssignees.length + selectedStatuses.length + selectedPriorities.length + selectedReleases.length + selectedLabels.length + selectedScopes.length}
                                                            </Badge>
                                                        )}
                                    </button>
                                </SheetTrigger>
                                <SheetContent className="w-[400px] sm:w-[540px] flex flex-col">
                                    <SheetHeader className="pb-2">
                                        <SheetTitle>Advanced Filters</SheetTitle>
                                        <SheetDescription>
                                            Refine your task search with detailed filters
                                        </SheetDescription>
                                    </SheetHeader>
                                    
                                    {/* Scrollable filter content */}
                                    <ScrollArea className="flex-1 -mx-6 px-6">
                                        <div className="space-y-6 pb-6">
                                            {/* Organizations */}
                                            {filterOptions.organizations.length > 0 && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Organizations {selectedOrganizations.length > 0 && `(${selectedOrganizations.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {filterOptions.organizations.map(org => (
                                                                <div key={org.id} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                    <Checkbox
                                                                        id={`org-${org.id}`}
                                                                        checked={selectedOrganizations.includes(org.slug)}
                                                                        onCheckedChange={(checked) => {
                                                                            setSelectedOrganizations(prev => 
                                                                                checked 
                                                                                    ? [...prev, org.slug]
                                                                                    : prev.filter(o => o !== org.slug)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label
                                                                        htmlFor={`org-${org.id}`}
                                                                        className="text-sm cursor-pointer flex-1"
                                                                    >
                                                                        {org.name}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                            {/* Projects */}
                                            {filterOptions.projects.length > 0 && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Projects {selectedProjects.length > 0 && `(${selectedProjects.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {filterOptions.projects.map(project => (
                                                                <div key={project.id} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                    <Checkbox
                                                                        id={`project-${project.id}`}
                                                                        checked={selectedProjects.includes(project.id)}
                                                                        onCheckedChange={(checked) => {
                                                                            setSelectedProjects(prev => 
                                                                                checked 
                                                                                    ? [...prev, project.id]
                                                                                    : prev.filter(p => p !== project.id)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label
                                                                        htmlFor={`project-${project.id}`}
                                                                        className="text-sm cursor-pointer flex-1"
                                                                    >
                                                                        {project.name}
                                                                        <span className="text-xs text-muted-foreground ml-1">
                                                                            ({project.key})
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                            {/* Assignees */}
                                            {filterOptions.users.length > 0 && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Assignees {selectedAssignees.length > 0 && `(${selectedAssignees.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {/* Unassigned option */}
                                                            <div className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                <Checkbox
                                                                    id="assignee-unassigned"
                                                                    checked={selectedAssignees.includes('unassigned')}
                                                                    onCheckedChange={(checked) => {
                                                                        setSelectedAssignees(prev => 
                                                                            checked 
                                                                                ? [...prev, 'unassigned']
                                                                                : prev.filter(a => a !== 'unassigned')
                                                                        );
                                                                    }}
                                                                />
                                                                <label
                                                                    htmlFor="assignee-unassigned"
                                                                    className="text-sm cursor-pointer flex-1 text-muted-foreground italic"
                                                                >
                                                                    Unassigned
                                                                </label>
                                                            </div>
                                                            {filterOptions.users.map(user => (
                                                                <div key={user.id} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                    <Checkbox
                                                                        id={`assignee-${user.id}`}
                                                                        checked={selectedAssignees.includes(user.id)}
                                                                        onCheckedChange={(checked) => {
                                                                            setSelectedAssignees(prev => 
                                                                                checked 
                                                                                    ? [...prev, user.id]
                                                                                    : prev.filter(a => a !== user.id)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label
                                                                        htmlFor={`assignee-${user.id}`}
                                                                        className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                                                                    >
                                                                        <div 
                                                                            className={cn(
                                                                                "w-5 h-5 rounded-full flex items-center justify-center text-xs text-white",
                                                                                user.avatarColor
                                                                            )}
                                                                        >
                                                                            {user.name.charAt(0).toUpperCase()}
                                                                        </div>
                                                                        {user.name}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                            {/* Status */}
                                            {filterOptions.statuses.length > 0 && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Status {selectedStatuses.length > 0 && `(${selectedStatuses.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {filterOptions.statuses.map(status => {
                                                                const IconComponent = getIconComponent(status.icon);
                                                                const colorClass = STATUS_COLORS[status.color]?.text || 'text-gray-500';
                                                                
                                                                return (
                                                                    <div key={status.value} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                        <Checkbox
                                                                            id={`status-${status.value}`}
                                                                            checked={selectedStatuses.includes(status.value)}
                                                                            onCheckedChange={(checked) => {
                                                                                setSelectedStatuses(prev => 
                                                                                    checked 
                                                                                        ? [...prev, status.value]
                                                                                        : prev.filter(s => s !== status.value)
                                                                                );
                                                                            }}
                                                                        />
                                                                        <label
                                                                            htmlFor={`status-${status.value}`}
                                                                            className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                                                                        >
                                                                            {IconComponent && React.createElement(IconComponent, {
                                                                                className: `w-4 h-4 ${colorClass}`
                                                                            })}
                                                                            <span>{status.label}</span>
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                            {/* Priority */}
                                            {filterOptions.priorities.length > 0 && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Priority {selectedPriorities.length > 0 && `(${selectedPriorities.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {filterOptions.priorities.map(priority => {
                                                                const priorityConfig = getPriorityConfig(priority.value);
                                                                return (
                                                                    <div key={priority.value} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                        <Checkbox
                                                                            id={`priority-${priority.value}`}
                                                                            checked={selectedPriorities.includes(priority.value)}
                                                                            onCheckedChange={(checked) => {
                                                                                setSelectedPriorities(prev => 
                                                                                    checked 
                                                                                        ? [...prev, priority.value]
                                                                                        : prev.filter(p => p !== priority.value)
                                                                                );
                                                                            }}
                                                                        />
                                                                        <label
                                                                            htmlFor={`priority-${priority.value}`}
                                                                            className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                                                                        >
                                                                            {React.createElement(priorityConfig.icon, {
                                                                                className: `w-4 h-4 ${priorityConfig.color}`
                                                                            })}
                                                                            <span>{priority.label}</span>
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                            {/* Releases */}
                                            {filterOptions.releases.length > 0 && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Releases {selectedReleases.length > 0 && `(${selectedReleases.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {filterOptions.releases.map(release => (
                                                                <div key={release.id} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                    <Checkbox
                                                                        id={`release-${release.id}`}
                                                                        checked={selectedReleases.includes(release.id)}
                                                                        onCheckedChange={(checked) => {
                                                                            setSelectedReleases(prev => 
                                                                                checked 
                                                                                    ? [...prev, release.id]
                                                                                    : prev.filter(r => r !== release.id)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label
                                                                        htmlFor={`release-${release.id}`}
                                                                        className="text-sm cursor-pointer flex-1"
                                                                    >
                                                                        {release.name}
                                                                        {release.projectName && (
                                                                            <span className="text-xs text-muted-foreground ml-1">
                                                                                ({release.projectKey})
                                                                            </span>
                                                                        )}
                                                                        {release.isShipped && (
                                                                            <span className="text-xs text-emerald-600 ml-1">✓</span>
                                                                        )}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                              {/* Labels */}
                                              {filterOptions.labels.length > 0 && (
                                                      <div className="space-y-2">
                                                        <Label className="text-xs font-semibold">
                                                            Labels {selectedLabels.length > 0 && `(${selectedLabels.length})`}
                                                        </Label>
                                                        <div className="border rounded-md p-2 space-y-1">
                                                            {filterOptions.labels.map(label => {
                                                                const normalizedColor = normalizeLabelColor(label.color);
                                                                return (
                                                                <div key={label.name} className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                                    <Checkbox
                                                                        id={`label-${label.name}`}
                                                                        checked={selectedLabels.includes(label.name)}
                                                                        onCheckedChange={(checked) => {
                                                                            setSelectedLabels(prev => 
                                                                                checked 
                                                                                    ? [...prev, label.name]
                                                                                    : prev.filter(l => l !== label.name)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label
                                                                        htmlFor={`label-${label.name}`}
                                                                        className="text-sm cursor-pointer flex-1 flex items-center gap-2 min-w-0"
                                                                    >
                                                                        <span 
                                                                            className={cn(
                                                                                "w-3 h-3 rounded-sm flex-shrink-0",
                                                                                LABEL_COLOR_MAP[normalizedColor] || LABEL_COLOR_MAP['gray']
                                                                            )}
                                                                        />
                                                                        <span className="flex-1 min-w-0 flex items-baseline gap-1.5">
                                                                            <span className="font-medium">{label.name}</span>
                                                                            {label.projectKeys && label.projectKeys.length > 0 && (
                                                                                <span 
                                                                                    className="text-[11px] text-muted-foreground whitespace-nowrap"
                                                                                    title={label.projectKeys.length > 2 ? `Used in: ${label.projectKeys.join(', ')}` : undefined}
                                                                                >
                                                                                    {label.projectKeys.length === 1 ? (
                                                                                        `(${label.projectKeys[0]})`
                                                                                    ) : (
                                                                                        <>({label.projectKeys.slice(0, 2).join(', ')}
                                                                                        {label.projectKeys.length > 2 && (
                                                                                            <span className="text-muted-foreground/70">
                                                                                                {' '}+{label.projectKeys.length - 2}
                                                                                            </span>
                                                                                        )})</>
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                            )}
                                            
                                            {/* Scope Filter */}
                                            <div className="space-y-2">
                                                <Label className="text-xs font-semibold">
                                                    Scope {selectedScopes.length > 0 && `(${selectedScopes.length})`}
                                                </Label>
                                                <div className="border rounded-md p-2 space-y-1">
                                                    <div className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                        <Checkbox
                                                            id="scope-active"
                                                            checked={selectedScopes.includes('active')}
                                                            onCheckedChange={(checked) => {
                                                                setSelectedScopes(prev => 
                                                                    checked 
                                                                        ? [...prev, 'active']
                                                                        : prev.filter(s => s !== 'active')
                                                                );
                                                            }}
                                                        />
                                                        <label
                                                            htmlFor="scope-active"
                                                            className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                                                        >
                                                            <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                                            Active Week
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                        <Checkbox
                                                            id="scope-backlog"
                                                            checked={selectedScopes.includes('backlog')}
                                                            onCheckedChange={(checked) => {
                                                                setSelectedScopes(prev => 
                                                                    checked 
                                                                        ? [...prev, 'backlog']
                                                                        : prev.filter(s => s !== 'backlog')
                                                                );
                                                            }}
                                                        />
                                                        <label
                                                            htmlFor="scope-backlog"
                                                            className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                                                        >
                                                            <Archive className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                                                            Backlog
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center space-x-2 p-1 hover:bg-muted rounded">
                                                        <Checkbox
                                                            id="scope-closed"
                                                            checked={selectedScopes.includes('closed')}
                                                            onCheckedChange={(checked) => {
                                                                setSelectedScopes(prev => 
                                                                    checked 
                                                                        ? [...prev, 'closed']
                                                                        : prev.filter(s => s !== 'closed')
                                                                );
                                                            }}
                                                        />
                                                        <label
                                                            htmlFor="scope-closed"
                                                            className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                                                        >
                                                            <XCircle className="w-4 h-4 text-muted-foreground" />
                                                            Closed
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </ScrollArea>
                                    
                                    {/* Footer with Apply button */}
                                    <div className="mt-2 pt-2 border-t flex items-center gap-2">
                                        <Button
                                            onClick={() => setFiltersOpen(false)}
                                            className="flex-1"
                                        >
                                            Apply Filters
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={clearAllFilters}
                                        >
                                            Clear All
                                        </Button>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                        
                        {/* Active Week Only Switch - Right Side */}
                        <div className="flex items-center gap-2 ml-auto">
                            <Switch
                                id="active-week-only"
                                checked={activeWeekOnly}
                                onCheckedChange={setActiveWeekOnly}
                                className={cn(
                                    "scale-75",
                                    activeWeekOnly && "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                                )}
                            />
                            <Label 
                                htmlFor="active-week-only" 
                                className={cn(
                                    "text-xs cursor-pointer whitespace-nowrap transition-colors",
                                    activeWeekOnly 
                                        ? "text-emerald-600 dark:text-emerald-400 font-medium" 
                                        : "text-muted-foreground"
                                )}
                            >
                                Active week only
                            </Label>
                        </div>
                    </div>
                        
                    {/* Active Filter Chips - Always Visible */}
                    <div className="min-h-[3rem] flex flex-wrap items-center gap-2 py-2 px-3 bg-muted/20 border border-border/50 rounded-lg overflow-auto">
                        {activeFilters.length > 0 ? (
                            <>
                                {activeFilters.map(filter => (
                                    <div
                                        key={filter.id}
                                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-muted/50 border border-muted-foreground/10 text-foreground transition-all hover:bg-muted"
                                    >
                                        {filter.type && (
                                            <span className="text-[10px] font-medium text-muted-foreground uppercase">
                                                {filter.type}
                                            </span>
                                        )}
                                        <span className="text-xs">{filter.label}</span>
                                        <button
                                            onClick={filter.onRemove}
                                            className="p-0.5 rounded hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
                                            aria-label={`Remove ${filter.label} filter`}
                                        >
                                            <FiX className="h-2.5 w-2.5" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={clearAllFilters}
                                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Clear all
                                </button>
                            </>
                        ) : (
                            <span className="text-xs text-muted-foreground">
                                No filters applied
                            </span>
                        )}
                    </div>
                </div>

                <motion.div 
                    variants={fadeInVariants}
                    transition={{ delay: 0.2 }}
                >
                    {loading && tasks.length === 0 ? (
                        <Card className="shadow-sm">
                            <CardContent className="p-6">
                                <div className="space-y-3">
                                    <Skeleton className="h-8 w-full" />
                                    {[...Array(4)].map((_, i) => (
                                        <Skeleton key={i} className="h-16 w-full" />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ) : tasks.length > 0 ? (
                        <div className="relative">
                            <div
                                ref={scrollContainerRef}
                                className="w-full overflow-x-auto"
                            >
                                <div className="rounded-md border bg-card shadow-sm table min-w-full">
                                    <Table horizontalScroll={false}>
                                        <TableHeader>
                                            <TableRow className="group hover:bg-accent transition-none">
                                                <TableHead className="whitespace-nowrap">Key</TableHead>
                                                <TableHead className="max-w-[400px]">Summary</TableHead>
                                                <TableHead className="whitespace-nowrap">Project</TableHead>
                                                <TableHead className="whitespace-nowrap">Status</TableHead>
                                                <TableHead className="whitespace-nowrap">Scope</TableHead>
                                                <TableHead className="whitespace-nowrap">Assignee</TableHead>
                                                <TableHead className="whitespace-nowrap">Due Date</TableHead>
                                                <TableHead className="whitespace-nowrap">Priority</TableHead>
                                                <TableHead className="whitespace-nowrap">Labels</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {tasks.map((task) => (
                                                <TaskContextMenu key={task.id} task={task} organizationSlug={task.organization?.slug}>
                                                    <TableRow
                                                        className="group hover:bg-muted/30 transition-none"
                                                    >
                                                        <TableCell className="whitespace-nowrap">
                                                            <a
                                                                href={`/${task.organization?.slug}/plan/${task.key}`}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    navigate(`/${task.organization?.slug}/plan/${task.key}`);
                                                                }}
                                                                className="inline-flex items-center px-2 py-1 rounded-md bg-muted/50 hover:bg-primary/10 hover:text-primary text-xs text-muted-foreground font-mono border border-border/50 hover:border-primary/30 transition-colors cursor-pointer"
                                                            >
                                                                {task.key}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell className="font-medium max-w-[400px]">
                                                            <a
                                                                href={`/${task.organization?.slug}/plan/${task.key}`}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    navigate(`/${task.organization?.slug}/plan/${task.key}`);
                                                                }}
                                                                className="text-left hover:underline transition-colors block truncate"
                                                                title={task.summary}
                                                            >
                                                                {task.summary}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-medium">{task.project?.name}</span>
                                                                <span className="text-xs text-muted-foreground">{task.organization?.name}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            {(() => {
                                                                // Use project-specific status config from the API
                                                                const statusConfig = task.statusConfig;

                                                                if (statusConfig) {
                                                                    const IconComponent = getIconComponent(statusConfig.icon);
                                                                    const colorClass = STATUS_COLORS[statusConfig.color]?.text || 'text-gray-500';

                                                                    return (
                                                                        <div className="flex items-center gap-1.5">
                                                                            {IconComponent && React.createElement(IconComponent, {
                                                                                className: `w-4 h-4 ${colorClass}`
                                                                            })}
                                                                            <span className="text-sm">{statusConfig.name}</span>
                                                                        </div>
                                                                    );
                                                                }

                                                                // Fallback for when statusConfig isn't available
                                                                return (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {task.status}
                                                                    </Badge>
                                                                );
                                                            })()}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            {task.closedAt ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <XCircle className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                                                                    <span className="text-sm text-muted-foreground">Closed</span>
                                                                </div>
                                                            ) : task.release ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <FiPackage className={cn(
                                                                        "w-4 h-4 flex-shrink-0",
                                                                        getReleaseStatusColor(getReleaseStatus(task.release)).icon
                                                                    )} />
                                                                    <span className="text-sm" title={task.release.name}>
                                                                        {task.release.name}
                                                                    </span>
                                                                </div>
                                                            ) : task.scope === 'backlog' ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <Archive className="w-4 h-4 flex-shrink-0 text-neutral-600 dark:text-neutral-400" />
                                                                    <span className="text-sm">Backlog</span>
                                                                </div>
                                                            ) : isCompletedInPastWeek(task) ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <TbCircleCheck className="w-4 h-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                                                                    <span className="text-sm">Previous Week</span>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-1.5">
                                                                    <Zap className="w-4 h-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                                                                    <span className="text-sm">Active Week</span>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            {task.assignee ? (
                                                                <div className="flex items-center gap-2">
                                                                    <Avatar className="w-4 h-4">
                                                                        <AvatarImage src={task.assignee.avatar} />
                                                                        <AvatarFallback className={`${task.assignee.avatarColor || 'bg-neutral-600'} text-neutral-200 text-[0.5rem]`}>
                                                                            {task.assignee.name.charAt(0).toUpperCase()}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <span className="text-sm">{task.assignee.name}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted-foreground text-sm">Unassigned</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            {task.dueDate ? (
                                                                <div className="flex flex-col gap-0.5" data-due-date-json={JSON.stringify(task.dueDate)}>
                                                                    <span className={cn(
                                                                        "text-sm whitespace-nowrap",
                                                                        task.dueDate.isOverdue && "text-red-600 dark:text-red-400 font-medium",
                                                                        task.dueDate.isDueSoon && "text-amber-600 dark:text-amber-400 font-medium"
                                                                    )}>
                                                                        {task.dueDate.formatted}
                                                                    </span>

                                                                    {/* Show completion status or source - prioritize release status */}
                                                                    {task.dueDate.wasReleasedOnTime === true ? (
                                                                        <span className="text-[11px] text-emerald-600 dark:text-emerald-500 whitespace-nowrap">
                                                                            ✓ shipped on time
                                                                        </span>
                                                                    ) : task.dueDate.wasReleasedOnTime === false ? (
                                                                        <span className="text-[11px] text-emerald-600 dark:text-emerald-500 whitespace-nowrap">
                                                                            ✓ shipped late
                                                                        </span>
                                                                    ) : task.dueDate.wasCompletedOnTime === true ? (
                                                                        <span className="text-[11px] text-emerald-600 dark:text-emerald-500 whitespace-nowrap">
                                                                            ✓ done on time
                                                                        </span>
                                                                    ) : task.dueDate.wasCompletedOnTime === false ? (
                                                                        <span className="text-[11px] text-emerald-600 dark:text-emerald-500 whitespace-nowrap">
                                                                            ✓ done late
                                                                        </span>
                                                                    ) : task.dueDate.source && task.dueDate.source !== 'task' ? (
                                                                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                                                            {task.dueDate.source === 'release' ? 'from release' :
                                                                                task.dueDate.source === 'both' ? 'task + release' : ''}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted-foreground text-sm">—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            {(() => {
                                                                const priorityConfig = getPriorityConfig(task.priority);
                                                                return (
                                                                    <div className="flex items-center gap-1.5">
                                                                        {React.createElement(priorityConfig.icon, {
                                                                            className: `w-4 h-4 ${priorityConfig.color}`
                                                                        })}
                                                                        <span className="text-sm">{priorityConfig.name}</span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {task.labels && task.labels.length > 0 ? (
                                                                <div className="flex flex-wrap gap-1.5 max-w-[250px]">
                                                                     {task.labels.slice(0, 3).map((labelId) => {
                                                                         // Find label details from project
                                                                         const labelDetails = task.project?.labels?.find(l => l._id === labelId || l.id === labelId);
                                                                         if (!labelDetails) return null;
 
                                                                         const normalizedColor = normalizeLabelColor(labelDetails.color);
 
                                                                         return (
                                                                             <div
                                                                                 key={labelDetails._id || labelDetails.id}
                                                                                 className="text-[11px] px-1.5 h-5 border border-border rounded-sm inline-flex items-center text-muted-foreground gap-1.5 min-w-0 max-w-[120px]"
                                                                                 title={labelDetails.name}
                                                                             >
                                                                                 <div
                                                                                     className={cn(
                                                                                         "w-2 h-2 rounded-full shrink-0",
                                                                                         LABEL_COLOR_MAP[normalizedColor] || LABEL_COLOR_MAP['gray']
                                                                                     )}
                                                                                 />
                                                                                 <span className="truncate">{labelDetails.name}</span>
                                                                             </div>
                                                                         );
                                                                     })}
                                                                    {task.labels.length > 3 && (
                                                                        <div className="text-[11px] px-1.5 h-5 border border-border rounded-sm inline-flex items-center text-muted-foreground">
                                                                            +{task.labels.length - 3}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted-foreground text-sm">—</span>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </TaskContextMenu>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>

                            </div>

                            {/* Pagination Controls */}
                            {pagination.totalPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-4 border-t bg-card">
                                    <div className="text-sm text-muted-foreground">
                                        Page {currentPage} of {pagination.totalPages}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={!pagination.hasPrev || refreshing}
                                        >
                                            <FiChevronLeft className="h-4 w-4 mr-1" />
                                            Previous
                                        </Button>

                                        {/* Page numbers */}
                                        <div className="hidden sm:flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= pagination.totalPages - 2) {
                                                    pageNum = pagination.totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }

                                                return (
                                                    <Button
                                                        key={pageNum}
                                                        variant={currentPage === pageNum ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => handlePageChange(pageNum)}
                                                        disabled={refreshing}
                                                        className="w-9 h-9 p-0"
                                                    >
                                                        {pageNum}
                                                    </Button>
                                                );
                                            })}
                                        </div>

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={!pagination.hasNext || refreshing}
                                        >
                                            Next
                                            <FiChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Conditional fade effects */}
                            {showRightShadow && (
                                <div className="absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent pointer-events-none" />
                            )}

                            {showLeftShadow && (
                                <div className="absolute top-0 left-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent pointer-events-none" />
                            )}
                        </div>
                    ) : !loading ? (
                        <Card className="shadow-sm">
                            <CardContent className="p-6">
                                <div className="text-center py-12">
                                    {activeFilterCount > 0 || searchTerm ? (
                                        <>
                                            <h3 className="text-lg font-semibold mb-2">No matching tasks</h3>
                                            <p className="text-muted-foreground mb-4">
                                                Try adjusting your search or filter criteria.
                                            </p>
                                            {activeFilterCount > 0 && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={clearAllFilters}
                                                >
                                                    Clear all filters
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <h3 className="text-lg font-semibold mb-2">No tasks yet</h3>
                                            <p className="text-muted-foreground">
                                                Tasks will appear here once they are created.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}
                </motion.div>
            </motion.div>
            
            {/* Bot Sort Info Modal */}
            <Dialog open={showBotSortInfo} onOpenChange={setShowBotSortInfo}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2.5 text-xl">
                            <StatusBotIcon className="w-6 h-6 text-primary" />
                            <span>Bot Sort</span>
                            <Badge variant="secondary" className="text-xs font-normal">Beta</Badge>
                        </DialogTitle>
                        <DialogDescription className="text-base pt-2">
                            An intelligent sorting algorithm that prioritizes your tasks based on multiple factors.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-2">
                        <div className="space-y-3">
                            <h4 className="font-semibold text-sm">How it works</h4>
                            <div className="space-y-3 text-sm">
                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">
                                        1
                                    </div>
                                    <div>
                                        <div className="font-medium mb-0.5">Priority Level</div>
                                        <div className="text-muted-foreground text-xs">Urgent tasks rank highest, followed by high, medium, and low priority.</div>
                                    </div>
                                </div>
                                
                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">
                                        2
                                    </div>
                                    <div>
                                        <div className="font-medium mb-0.5">Due Date Urgency</div>
                                        <div className="text-muted-foreground text-xs">Overdue and soon-to-be-due tasks get extra attention.</div>
                                    </div>
                                </div>
                                
                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">
                                        3
                                    </div>
                                    <div>
                                        <div className="font-medium mb-0.5">Assignment & Activity</div>
                                        <div className="text-muted-foreground text-xs">Tasks assigned to you and recently updated items are boosted.</div>
                                    </div>
                                </div>
                                
                                <div className="flex gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">
                                        4
                                    </div>
                                    <div>
                                        <div className="font-medium mb-0.5">Work Status</div>
                                        <div className="text-muted-foreground text-xs">In-progress and blocked tasks are prioritized over backlog items.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">
                                Bot Sort is experimental and continuously improving based on your feedback.
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex justify-end pt-2">
                        <Button onClick={handleCloseBotSortInfo} className="px-6">
                            Got it!
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default Tasks;
